// Google ID-token helpers for the dashboard login gate.
// Pure functions + localStorage access scoped to Google keys ONLY — never touches
// the CRIO (`crio_auth_tokens`) or admin-key (`flash_admin_key`) storage.

const ID_TOKEN_KEY = 'flash_google_id_token';
const PROFILE_KEY = 'flash_google_profile';

export interface GoogleProfile {
  email: string;
  name?: string;
  picture?: string;
  hd?: string; // Google Workspace hosted domain
  email_verified?: boolean;
  exp?: number; // seconds since epoch
}

/** Decode a JWT payload (base64url). Returns null on any malformation. */
export function decodeJwt(token: string): GoogleProfile | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as GoogleProfile;
  } catch {
    return null;
  }
}

/** True if the token is missing/malformed or its `exp` is in the past (30s skew). */
export function isExpired(token: string | null): boolean {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 <= Date.now() + 30_000;
}

/** Enforce the @delricht.com Workspace domain: hosted-domain claim + verified email suffix. */
export function isAllowedDomain(payload: GoogleProfile | null, domain: string): boolean {
  if (!payload) return false;
  const emailOk = !!payload.email && payload.email.toLowerCase().endsWith('@' + domain.toLowerCase());
  const hdOk = (payload.hd || '').toLowerCase() === domain.toLowerCase();
  // Require BOTH the hd claim and the email suffix, plus a verified email.
  return emailOk && hdOk && payload.email_verified !== false;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(ID_TOKEN_KEY);
}

export function getStoredProfile(): GoogleProfile | null {
  const raw = localStorage.getItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as GoogleProfile;
  } catch {
    return null;
  }
}

export function storeIdentity(token: string, profile: GoogleProfile): void {
  localStorage.setItem(ID_TOKEN_KEY, token);
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

/** Clear ONLY the Google keys. Never clears crio_auth_tokens / flash_admin_key. */
export function clearIdentity(): void {
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
}
